import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchAudienceByCountry,
  clearAudienceByCountry,
} from 'redux/analytics/actions';

import {
  ComposableMap,
  Geographies,
  Geography,
} from 'react-simple-maps';
import ReactTooltip from 'react-tooltip';

import { Loader } from 'semantic-ui-react';
import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import { countries as countriesFakeData } from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

import geographyData from './static/world-50m.json';

const AnalyticsAudienceByCountry = ({
  date,
  audienceByCountry,
  audienceByCountryError,
  fetchAudienceByCountry,
  clearAudienceByCountry,
  me,
}) => {
  const { t } = useTranslation();
  const [tooltipText, setTooltipText] = useState("");

  useEffect(() => {
    fetchAudienceByCountry(date);
    return () => clearAudienceByCountry();
  }, [date]);

  if (audienceByCountryError) {
    return null;
  }
  if (audienceByCountry && Object.keys(audienceByCountry).length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("Views by Country (map)")} text={t("Looks like you have no data to display yet…")} />;
  }

  const renderMap = (audienceByCountryData) => {
    if (!audienceByCountryData) {
      return null;
    }

    const colors = [
      '#c8dcf4',
      '#a7c7ee',
      '#97bdea',
      '#87b3e7',
      '#76a8e4',
      '#5594dd',
      '#357fd7',
      '#2975cf',
      '#266cbf',
      '#1f599e',
    ];

    const data = Object.keys(audienceByCountryData).map(i => ({
      label: i,
      value: audienceByCountryData[i].views,
    }));

    return (
      <>
        <ComposableMap
          data-tip=""
          projectionConfig={{
            scale: 205,
            rotation: [-11, 0, 0],
          }}
          width={980}
          height={551}
          style={{
            width: '100%',
            height: 'auto',
          }}
        >
          <Geographies geography={geographyData}>
            {({ geographies }) => geographies.map((geography) => {
              const calculateColor = () => {
                const index = data.map(country => country.label)
                  .indexOf(geography.properties.name);
                if (index !== -1) {
                  return colors[index] || colors[colors.length - 1];
                }
                return '#dde2eb';
              };

              return (
                <Geography
                  key={`country_geography_key_for_${geography.properties.name.toLowerCase().replace(/\s+/g, '')}`}
                  geography={geography}
                  onMouseEnter={() => {
                    const { name } = geography.properties;
                    if (Object.keys(audienceByCountryData).indexOf(name) >= 0) {
                      const tooltipLabel = audienceByCountryData[name].views === 1 ? t("view") : t("views");
                      setTooltipText(`${name}: ${Math.round(audienceByCountryData[name].percent)}% (${audienceByCountryData[name].views} ${tooltipLabel})`);
                    }
                  }}
                  onMouseLeave={() => {
                    setTooltipText("");
                  }}
                  style={{
                    default: {
                      fill: calculateColor(),
                      stroke: '#fefefe',
                      strokeWidth: 0.75,
                      outline: 'none',
                    },
                    hover: {
                      fill: calculateColor(),
                    },
                  }}
                />
              );
            })}
          </Geographies>
        </ComposableMap>

        {/* Countries Tooltips */}
        <ReactTooltip>{tooltipText}</ReactTooltip>
      </>
    );
  };

  return (
    <AnalyticsSection
      className="analytics-audience-by-country__component"
      header={
        <div className="title">
          <h3>{t("Views by Country (map)")}</h3>
        </div>
      }
    >
      <div className="analytics-audience-by-country-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderMap(countriesFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderMap(audienceByCountry)}
          </>
        }
        <Loader active={!audienceByCountry} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsAudienceByCountry.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchAudienceByCountry: PropTypes.func.isRequired,
  clearAudienceByCountry: PropTypes.func.isRequired,
  audienceByCountry: PropTypes.oneOfType([PropTypes.object]),
  audienceByCountryError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
};
AnalyticsAudienceByCountry.defaultProps = {
  audienceByCountry: null,
  audienceByCountryError: null,
};

const mapStateToProps = state => ({
  audienceByCountry: state.analyticsReducer.audienceByCountry,
  audienceByCountryError: state.analyticsReducer.audienceByCountryError,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchAudienceByCountry,
    clearAudienceByCountry,
  },
)(AnalyticsAudienceByCountry);
